import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';

const MetalRoofFlashingForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  // Define all the form fields based on the image
  const [formData, setFormData] = useState({
    customer: 'Topsail roofing',
    orderPO: '',
    panelProfile: '',
    jobName: '',
    jobAddress: '',
    jobNumber: '',
    flashings: {
      eaveStandard: {
        selected: false,
        roofPitch: '',
        color: '',
        quantity: '',
        pricePerFt: '',
        specialOrder: false
      },
      eaveHook: {
        selected: false,
        roofPitch: '',
        color: '',
        quantity: '',
        pricePerFt: '',
        specialOrder: false
      },
      ridgeStandard: {
        selected: false,
        roofPitch: '',
        color: '',
        quantity: '',
        pricePerFt: '',
        specialOrder: false
      },
      hipStandard: {
        selected: false,
        roofPitch: '',
        color: '',
        quantity: '',
        pricePerFt: '',
        specialOrder: false
      },
      zeeClosure: {
        selected: false,
        roofPitch: '',
        color: '',
        quantity: '',
        pricePerFt: '',
        specialOrder: false
      }
    }
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'metalRoofFlashing') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          const projectData = await fetchProjectData(projectId);
          const prefilled = prefillFormData(projectData, formData);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFlashingChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      flashings: {
        ...prev.flashings,
        [type]: {
          ...prev.flashings[type],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Metal Roof Flashing form updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'metalRoofFlashing',
          formData
        })).unwrap();
        showToast('Metal Roof Flashing form saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Metal Roof Flashing form', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderFlashingSection = (type, title, code) => {
    const flashing = formData.flashings[type];
    
    return (
      <div className="border-b border-gray-300 py-4">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-1 flex items-center">
            <div className="flex flex-col items-center">
              <input
                type="checkbox"
                checked={flashing.selected}
                onChange={(e) => handleFlashingChange(type, 'selected', e.target.checked)}
                className="mb-2 h-5 w-5"
              />
              <span className="text-sm font-bold">{code}</span>
            </div>
          </div>
          
          <div className="col-span-5">
            <div className="text-center font-bold mb-4">{title}</div>
            
            {/* Placeholder for demonstration image */}
            {/* 
              Image will be added later. This is where the diagram for each flashing type will go.
              For now, we're leaving it as a commented placeholder.
            */}
            <div className="h-40 border border-dashed border-gray-300 flex items-center justify-center">
              <span className="text-gray-400">Diagram placeholder</span>
            </div>
          </div>
          
          <div className="col-span-6">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-2 font-bold">ROOF PITCH</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={flashing.roofPitch}
                      onChange={(e) => handleFlashingChange(type, 'roofPitch', e.target.value)}
                      className="w-full p-1 border-0"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 font-bold">COLOR</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={flashing.color}
                      onChange={(e) => handleFlashingChange(type, 'color', e.target.value)}
                      className="w-full p-1 border-0"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 font-bold">QUANTITY ea</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={flashing.quantity}
                      onChange={(e) => handleFlashingChange(type, 'quantity', e.target.value)}
                      className="w-full p-1 border-0"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 font-bold">PRICE per ft.</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={flashing.pricePerFt}
                      onChange={(e) => handleFlashingChange(type, 'pricePerFt', e.target.value)}
                      className="w-full p-1 border-0"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 font-bold">S/O</td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="checkbox"
                      checked={flashing.specialOrder}
                      onChange={(e) => handleFlashingChange(type, 'specialOrder', e.target.checked)}
                      className="h-5 w-5"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-7xl mx-auto">
      <div className="text-center font-bold text-xl mb-6">STANDARD TMP STANDING SEAM FLASHINGS</div>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <div className="grid grid-cols-3 gap-4">
              <div className="font-bold">Customer:</div>
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.customer}
                  onChange={(e) => handleChange('customer', e.target.value)}
                  className="w-full border border-gray-300 p-2"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div className="font-bold">Order/PO#:</div>
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.orderPO}
                  onChange={(e) => handleChange('orderPO', e.target.value)}
                  className="w-full border border-gray-300 p-2"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div className="font-bold">Panel Profile:</div>
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.panelProfile}
                  onChange={(e) => handleChange('panelProfile', e.target.value)}
                  className="w-full border border-gray-300 p-2"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div className="font-bold">Job Name:</div>
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.jobName}
                  onChange={(e) => handleChange('jobName', e.target.value)}
                  className="w-full border border-gray-300 p-2"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div className="font-bold">Job Address:</div>
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.jobAddress}
                  onChange={(e) => handleChange('jobAddress', e.target.value)}
                  className="w-full border border-gray-300 p-2"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div className="font-bold">Job Number:</div>
              <div className="col-span-2">
                <input
                  type="text"
                  value={formData.jobNumber}
                  onChange={(e) => handleChange('jobNumber', e.target.value)}
                  className="w-full border border-gray-300 p-2"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-center items-center">
            {/* Placeholder for Taylor Metal Products logo */}
            {/* 
              Logo will be added later. This is where the Taylor Metal Products logo will go.
              For now, we're leaving it as a commented placeholder.
            */}
            <div className="h-40 w-60 border border-dashed border-gray-300 flex items-center justify-center">
              <span className="text-gray-400">Logo placeholder</span>
            </div>
          </div>
        </div>
        
        {renderFlashingSection('eaveStandard', 'EAVE STANDARD', '__ __ ES')}
        {renderFlashingSection('eaveHook', 'EAVE HOOK (extended eave)', '__ __ EH')}
        {renderFlashingSection('ridgeStandard', 'RIDGE STANDARD', '__ __ RS')}
        {renderFlashingSection('hipStandard', 'HIP STANDARD', '__ __ HS')}
        {renderFlashingSection('zeeClosure', 'ZEE CLOSURE', '__ __ ZC')}
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : currentPermit && currentPermit._id ? 'Update' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MetalRoofFlashingForm;
